import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'saanti-public-brand';
const PublicBrandContext = createContext(null);

// Keep each property's public photography beside its branch identity. New
// properties can be added without scattering brand-specific paths throughout
// the page components, and a missing collection stays isolated instead of
// borrowing imagery from another property.
export const PUBLIC_PHOTOS = {
  saanti: {
    chooser: '/spa-images/saanti-2026/exterior.webp',
    hero: '/spa-images/saanti-2026/hero.webp',
    story: ['/spa-images/saanti-2026/exterior.webp', '/spa-images/saanti-2026/reception.webp'],
    welcome: ['/spa-images/saanti-2026/hero.webp', '/spa-images/saanti-2026/exterior.webp'],
    treatments: [
      '/spa-images/saanti-2026/massage.webp',
      '/spa-images/saanti-2026/oil-massage.webp',
      '/spa-images/saanti-2026/head-massage.webp',
      '/spa-images/saanti-2026/foot-treatment.webp',
      '/spa-images/saanti-2026/herbal-care.webp',
      '/spa-images/saanti-2026/therapist.webp',
    ],
    facilitiesHero: '/spa-images/saanti-2026/hero.webp',
    facilities: [
      '/spa-images/saanti-2026/foot-lounge.webp',
      '/spa-images/saanti-2026/hero.webp',
      '/spa-images/saanti-2026/private-room.webp',
    ],
    gallery: [
      '/spa-images/saanti-2026/reception.webp',
      '/spa-images/saanti-2026/hero.webp',
      '/spa-images/saanti-2026/foot-lounge.webp',
      '/spa-images/saanti-2026/private-room.webp',
      '/spa-images/saanti-2026/details.webp',
      '/spa-images/saanti-2026/tea.webp',
    ],
    contact: '/spa-images/saanti-2026/exterior.webp',
  },
  'saanti-boutique': {
    chooser: '/spa-images/saanti-boutique-2026/exterior.webp',
    hero: '/spa-images/saanti-boutique-2026/foot-lounge.webp',
    story: [
      '/spa-images/saanti-boutique-2026/exterior.webp',
      '/spa-images/saanti-boutique-2026/thai-room.webp',
    ],
    welcome: [
      '/spa-images/saanti-boutique-2026/thai-room.webp',
      '/spa-images/saanti-boutique-2026/back-massage.webp',
    ],
    treatments: [
      '/spa-images/saanti-boutique-2026/back-massage.webp',
      '/spa-images/saanti-boutique-2026/oil-massage.webp',
      '/spa-images/saanti-boutique-2026/head-massage.webp',
      '/spa-images/saanti-boutique-2026/herbal-compress.webp',
      '/spa-images/saanti-boutique-2026/hot-stone.webp',
      '/spa-images/saanti-boutique-2026/private-room.webp',
    ],
    facilitiesHero: '/spa-images/saanti-boutique-2026/thai-room.webp',
    facilities: [
      '/spa-images/saanti-boutique-2026/foot-lounge.webp',
      '/spa-images/saanti-boutique-2026/thai-room.webp',
      '/spa-images/saanti-boutique-2026/private-room.webp',
    ],
    gallery: [
      '/spa-images/saanti-boutique-2026/exterior.webp',
      '/spa-images/saanti-boutique-2026/foot-lounge.webp',
      '/spa-images/saanti-boutique-2026/thai-room.webp',
      '/spa-images/saanti-boutique-2026/heritage-bath.webp',
      '/spa-images/saanti-boutique-2026/courtyard.webp',
      '/spa-images/saanti-boutique-2026/private-room.webp',
    ],
    contact: '/spa-images/saanti-boutique-2026/entrance.webp',
  },
  nirana: {
    chooser: '/spa-images/nirana-2026/front-sign-close.webp',
    hero: '/spa-images/nirana-2026/massage.webp',
    story: ['/spa-images/nirana-2026/reception.webp', '/spa-images/nirana-2026/tea.webp'],
    welcome: ['/spa-images/nirana-2026/welcome.webp', '/spa-images/nirana-2026/tea.webp'],
    treatments: [
      '/spa-images/nirana-2026/massage.webp',
      '/spa-images/nirana-2026/thai-stretch.webp',
      '/spa-images/nirana-2026/back-massage.webp',
      '/spa-images/nirana-2026/foot-lounge.webp',
      '/spa-images/nirana-2026/products.webp',
      '/spa-images/nirana-2026/private-room.webp',
    ],
    facilitiesHero: '/spa-images/nirana-2026/hallway.webp',
    facilities: [
      '/spa-images/nirana-2026/foot-lounge.webp',
      '/spa-images/nirana-2026/shared-room.webp',
      '/spa-images/nirana-2026/private-room.webp',
    ],
    gallery: [
      '/spa-images/nirana-2026/shared-room.webp',
      '/spa-images/nirana-2026/foot-lounge.webp',
      '/spa-images/nirana-2026/private-room.webp',
      '/spa-images/nirana-2026/hallway.webp',
      '/spa-images/nirana-2026/shower.webp',
      '/spa-images/nirana-2026/rest-area.webp',
    ],
    contact: '/spa-images/nirana-2026/reception.webp',
  },
};

export const PUBLIC_LOCATIONS = [
  {
    id: 'saanti',
    label: 'SAANTI Thai Massage',
    shortLabel: 'SAANTI',
    branchName: 'SAANTI',
    branchCode: 'SONGWAT',
    kicker: 'Traditional Thai wellness · Song Wat',
    description: 'Traditional Thai massage and restorative treatments in the historic Song Wat neighbourhood.',
    image: PUBLIC_PHOTOS.saanti.chooser,
    photos: PUBLIC_PHOTOS.saanti,
    contact: {
      address: '981 Song Wat Road, Samphanthawong, Bangkok 10100',
      hours: '10:00–22:00',
      phoneDisplay: '+66 95 592 3236',
      phoneHref: 'tel:+66955923236',
      lineDisplay: '@saantisongwat',
      lineHref: 'https://line.me/R/ti/p/%40346gsmab',
      emailDisplay: 'sales.saanti@gmail.com',
      emailHref: 'mailto:sales.saanti@gmail.com',
    },
  },
  {
    id: 'saanti-boutique',
    label: 'SAANTI Boutique',
    shortLabel: 'SAANTI Boutique',
    branchName: 'SAANTI Boutique',
    branchCode: 'SUKHUMVIT',
    kicker: 'Boutique Thai massage · Yaowarat',
    description: 'A quiet Thai massage retreat inside Baan 2459 Heritage Boutique Hotel, steps from Yaowarat.',
    image: PUBLIC_PHOTOS['saanti-boutique'].chooser,
    photos: PUBLIC_PHOTOS['saanti-boutique'],
    contact: {
      address: '98 Phatsai, Samphanthawong, Bangkok',
      hours: '10:00–22:00',
      phoneDisplay: '+66 95 416 2369',
      phoneHref: 'tel:+66954162369',
      lineDisplay: '@saantiboutique',
      lineHref: 'https://line.me/R/ti/p/%40saantiboutique',
      instagramDisplay: '@saantiboutique',
      instagramHref: 'https://www.instagram.com/saantiboutique/',
      facebookDisplay: 'Saantiboutique Thai massage & spa',
      emailDisplay: 'saantiboutiquehouse@gmail.com',
      emailHref: 'mailto:saantiboutiquehouse@gmail.com',
      mapQuery: 'SAANTI Boutique Thai Massage House, 98 Phatsai, Samphanthawong, Bangkok',
    },
  },
  {
    id: 'nirana',
    label: 'NIRANA Massage & Spa',
    shortLabel: 'NIRANA',
    branchName: 'NIRANA',
    branchCode: 'NIRANA',
    kicker: 'Massage & Spa · Song Wat',
    description: 'A quiet, spacious retreat in Song Wat with a complete range of massage and spa treatments.',
    image: PUBLIC_PHOTOS.nirana.chooser,
    photos: PUBLIC_PHOTOS.nirana,
    contact: {
      address: '1063/5 Song Wat, Soi Wanit 1, Samphanthawong, Bangkok 10100',
      hours: '10:00–22:00',
      phoneDisplay: '+66 96 236 2623',
      phoneHref: 'tel:+66962362623',
      lineDisplay: '@niranaspa',
      lineHref: 'https://line.me/R/ti/p/%40niranaspa',
      instagramDisplay: '@niranaspa',
      instagramHref: 'https://www.instagram.com/niranaspa/',
      facebookDisplay: 'Niranapa',
      facebookHref: 'https://www.facebook.com/Niranapa',
      emailDisplay: 'niranaspa@gmail.com',
      emailHref: 'mailto:niranaspa@gmail.com',
      mapQuery: 'Nirana Spa, 1063/5 Song Wat, Soi Wanit 1, Samphanthawong, Bangkok 10100',
    },
  },
];

export const PUBLIC_BRANCH_CODES = PUBLIC_LOCATIONS.map((location) => location.branchCode);

const thaiPhoneDigits = (value) => {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('66')) digits = digits.slice(2);
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits;
};

export const thaiPhoneE164 = (value) => {
  const digits = thaiPhoneDigits(value);
  return digits ? `+66${digits}` : '';
};

export const formatThaiPhoneDisplay = (value) => {
  const digits = thaiPhoneDigits(value);
  if (!digits) return '';
  if (digits.length === 9) return `+66 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  if (digits.length === 8) return `+66 ${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4)}`;
  return `+66 ${digits}`;
};

export const normalizeBrand = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['saanti-boutique', 'saanti_boutique', 'boutique'].includes(normalized)) return 'saanti-boutique';
  if (normalized === 'nirana') return 'nirana';
  return 'saanti';
};

export const locationForBrand = (brand) => PUBLIC_LOCATIONS.find(
  (location) => location.id === normalizeBrand(brand),
) || PUBLIC_LOCATIONS[0];

export const photosForBrand = (brand) => locationForBrand(brand).photos;

export const locationForBranch = (branchOrCode) => {
  const code = typeof branchOrCode === 'string' ? branchOrCode : branchOrCode?.code;
  const normalized = String(code || '').trim().toUpperCase();
  return PUBLIC_LOCATIONS.find(
    (location) => location.branchCode.toUpperCase() === normalized,
  ) || null;
};

/**
 * Public/POS labels describe the business, while branch codes identify its
 * physical record. Unknown database branches retain their own saved name.
 */
export const publicBranchName = (branchOrCode) => {
  const registered = locationForBranch(branchOrCode);
  if (registered) return registered.branchName;
  if (typeof branchOrCode === 'string') return branchOrCode;
  return branchOrCode?.displayName
    || branchOrCode?.nameEn
    || branchOrCode?.name_en
    || branchOrCode?.nameTh
    || branchOrCode?.name_th
    || branchOrCode?.name
    || branchOrCode?.code
    || '';
};

export const isPublicBranch = (branchOrCode) => Boolean(locationForBranch(branchOrCode));

export const publicBrandFromSearch = (search) => {
  const value = new URLSearchParams(search).get('brand');
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  if (['saanti-boutique', 'saanti_boutique', 'boutique'].includes(normalized)) return 'saanti-boutique';
  if (normalized === 'nirana') return 'nirana';
  if (normalized === 'saanti') return 'saanti';
  return null;
};

export function PublicBrandProvider({ children }) {
  const location = useLocation();
  const [brand, setBrand] = useState(() => {
    const fromUrl = publicBrandFromSearch(window.location.search);
    if (fromUrl) return fromUrl;
    try { return normalizeBrand(window.localStorage.getItem(STORAGE_KEY)); }
    catch (_error) { return 'saanti'; }
  });

  useEffect(() => {
    const fromUrl = publicBrandFromSearch(location.search);
    if (fromUrl) setBrand(fromUrl);
  }, [location.search]);

  const selectBrand = useCallback((nextBrand) => {
    const normalized = normalizeBrand(nextBrand);
    setBrand(normalized);
    try { window.localStorage.setItem(STORAGE_KEY, normalized); }
    catch (_error) { /* Browsing still works when storage is unavailable. */ }
  }, []);

  const value = useMemo(() => ({ brand, selectBrand }), [brand, selectBrand]);
  return <PublicBrandContext.Provider value={value}>{children}</PublicBrandContext.Provider>;
}

export const usePublicBrand = () => {
  const value = useContext(PublicBrandContext);
  if (!value) throw new Error('usePublicBrand must be used inside PublicBrandProvider');
  return value;
};

export const brandForBranch = (branch) => {
  return locationForBranch(branch)?.id || null;
};

export const branchForBrand = (branches, brand) => {
  const list = branches || [];
  const location = locationForBrand(brand);
  const wantedCode = location.branchCode.toUpperCase();
  return list.find((branch) => String(branch.code || '').trim().toUpperCase() === wantedCode) || null;
};

export const brandPath = (path, brand) => {
  const [pathname, hash = ''] = String(path).split('#');
  const [base, existing = ''] = pathname.split('?');
  const params = new URLSearchParams(existing);
  // Branch identity is derived exclusively from the public-property registry.
  // Remove legacy branch parameters instead of carrying them between pages.
  ['branch', 'branchId', 'branchCode'].forEach((key) => params.delete(key));
  params.set('brand', normalizeBrand(brand));
  return `${base}?${params.toString()}${hash ? `#${hash}` : ''}`;
};

export const bookingPathForBrand = (_branches, brand) => {
  const params = new URLSearchParams({ brand: normalizeBrand(brand) });
  return `/book?${params.toString()}`;
};
